import { defineMessages, MessageDescriptor } from 'react-intl';
import { FilePollResponseStatus } from 'hellospa/page/prep-and-send/data/types';

export const responseMessages = defineMessages({
  tooManyPages: {
    id: '',
    description: 'Error message showing that a file has too many pages',
    defaultMessage: 'Page count must be under {maxPages} pages',
  },
  tooManyPagesLong: {
    id: '',
    description: 'Error message showing that a file has too many pages with cta',
    defaultMessage: 'Files must contain fewer than {maxPages} pages. Please click {cancel} and try again with a smaller file.',
  },
  passwordRequired: {
    id: '',
    description: 'Error Message when the uploaded file to convert is password protected',
    defaultMessage: 'This file requires a password. Please remove the password and try again.',
  },
  fileTooLarge: {
    id: '',
    description: 'Error Message when file to be imported are too large',
    defaultMessage: 'File size must be under {fileSize}MB',
  },
  fileTooLargeLong: {
    id: '',
    description: 'Error Message when file to be imported are too large with cta',
    defaultMessage: 'Files must be smaller than {fileSize} MB. Please click {cancel} and try again with a smaller file.',
  },
  retrieveError: {
    id: '',
    description: 'Error Message when there is an error retrieving a file that needs to be converted',
    defaultMessage: 'There was an error retrieving the file. Please try again later.',
  },
  badAuth: {
    id: '',
    description: 'Error message when the user is signed out with the external services, or the synchronization is not working',
    defaultMessage: 'We could not retrieve this file, please re-establish sync for this service {authUrl}',
  },
  badRequest: {
    id: '',
    description: 'Error message when the user is unable to retrieve file associated to an email from external services',
    defaultMessage: 'We could not retrieve this file, please re-establish sync for this service',
  },
  error: {
    id: '',
    description: 'Error Message when there is document conversion error',
    defaultMessage: 'We could not upload your file. Please try again or convert your file to PDF if you continue having problems.',
  },
});
type Meta = {
  isHelloFax: boolean,
  useLong?: boolean,
  maxFileSize?: number,
  authUrl?: string,
}

export type FileErrorDescriptor = {
  descriptor: MessageDescriptor,
  values?: Record<string, string | number>,
}

export type DeepIntegrationErrorDescriptor = {
  errorTitle: MessageDescriptor,
  errorInstructions: MessageDescriptor,
}

export default function getFileErrorMessage(
  error: FilePollResponseStatus,
  meta: Meta,
): FileErrorDescriptor {
  switch (error) {
    case FilePollResponseStatus.TooManyPages: {
      const maxPages = meta.isHelloFax ? 250 : 500;
      if (meta && meta.useLong) {
        return {
          descriptor: responseMessages.tooManyPagesLong,
          values: { maxPages },
        };
      }
      return {
        descriptor: responseMessages.tooManyPages,
        values: { maxPages },
      };
    }
    case FilePollResponseStatus.PasswordRequired:
      return {
        descriptor: responseMessages.passwordRequired,
      };
    case FilePollResponseStatus.FileTooLarge:
      if (meta && meta.useLong) {
        return {
          descriptor: responseMessages.fileTooLargeLong,
          values: { fileSize: meta.maxFileSize || 40 },
        };
      }
      return {
        descriptor: responseMessages.fileTooLarge,
        values: { fileSize: meta.maxFileSize || 40 },
      };
    case FilePollResponseStatus.RetrieveError:
      return {
        descriptor: responseMessages.retrieveError,
      };
    case FilePollResponseStatus.BadAuth:
      return {
        descriptor: responseMessages.badAuth,
        values: { authUrl: meta.authUrl || '' },
      };
    case FilePollResponseStatus.BadRequest:
      return {
        descriptor: responseMessages.badRequest,
      };
    case FilePollResponseStatus.Error:
    default:
      return {
        descriptor: responseMessages.error,
      };
  }
}
